import React from 'react';
import { Container, Typography, Box, Paper, Chip, Divider, Button } from '@mui/material';
import { SiteFooter } from '../components/SiteFooter';
import { Link as RouterLink } from 'react-router-dom';
import { visiblePlatforms } from '../config/platforms';
import { BrandLogo } from '../components/BrandLogo';

/**
 * What ShoutVector is, for anyone who lands on the site without an account.
 *
 * Public — no session required, like Privacy and Terms, and built the same way: a white
 * `Paper` on the page's grey background. The content lived on the login screen first, which
 * put a wall of explanation between a returning user and the sign-in form; it reads better
 * as its own page, reached from the About link on that form.
 *
 * The platform list is read from `visiblePlatforms()` rather than written out here, so it
 * cannot drift from what the app knows about. Every network renders the same way, which is a
 * presentation choice on a positioning page — the app itself still refuses to publish to a
 * `coming-soon` platform, so nothing downstream treats them as working.
 */

const FEATURES = [
  {
    title: 'Schedule ahead',
    body:
      'Queue posts against a weekly posting schedule, with timing guidance per network. ' +
      'A scheduler publishes them within about a minute of their time.',
  },
  {
    title: 'Know what actually went out',
    body:
      'Every post records its outcome per platform — published, refused before sending, or ' +
      'rejected by the network — so a partial post is never reported as a clean success.',
  },
  {
    title: 'Measure it',
    body:
      'Engagement and follower counts per account, refreshed hourly, with each figure ' +
      'traceable back to the post it came from.',
  },
  {
    title: 'Work as a team',
    body:
      'Everyone shares the same connections, posts and history, with an activity log ' +
      'recording what the app did and who asked for it.',
  },
];

export const About: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 } }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <BrandLogo maxWidth={260} height={56} />
        </Box>

        <Typography variant="h4" component="h1" align="center" gutterBottom>
          About ShoutVector
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          align="center"
          sx={{ maxWidth: 680, mx: 'auto', mb: 4 }}
        >
          ShoutVector publishes and schedules social media posts across several networks from
          one place. Write a post once, choose where it goes, and send it now or queue it for
          later — with a per-platform caption where one network's limit is tighter than the rest.
        </Typography>

        {/*
          Every platform the app knows about, rendered uniformly — the networks ShoutVector
          covers, without the page annotating build status.

          Note what this does NOT say: it does not claim every chip publishes today. Threads and
          Bluesky are still `coming-soon` in platforms.ts, and the app behaves accordingly —
          they are absent from the composer, and publishing to one is refused with
          `not_implemented` rather than faked. That boundary is deliberate and predates this
          page; see PUBLISHABLE_PLATFORMS in server/api.js and the post-outcomes model.

          If this app ever has users beyond its operator, revisit this: a brand-coloured chip
          reads as a working integration, and at that point the omission starts to mislead.
        */}
        <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1, mb: 5 }}>
          {visiblePlatforms().map(p => (
            <Chip
              key={p.id}
              label={p.name}
              variant="outlined"
              sx={{ borderColor: p.color, color: p.color }}
            />
          ))}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 4,
            mb: 5,
          }}
        >
          {FEATURES.map(f => (
            <Box key={f.title}>
              <Typography variant="h6" component="h2" gutterBottom>
                {f.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {f.body}
              </Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/*
          The attribution uses REMAGINE's own positioning language from remagine.com rather
          than copy invented here — the build/deploy/maintain line and the services framing are
          how the company describes itself on its own site. Keeping the wording
          in step with the parent site is the point; if remagine.com's positioning changes,
          this should follow it.
        */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="overline" color="text.secondary" display="block" sx={{ letterSpacing: 1.5 }}>
            Built by
          </Typography>

          {/*
            The name carries the positioning, so the separate "Your Internet Business Solution"
            line that used to sit under it would now just repeat itself and has gone.
          */}
          <Typography variant="h6" component="p" sx={{ fontWeight: 700, mb: 2 }}>
            <a
              href="https://www.remagine.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              REMAGINE <Box component="span" sx={{ mx: 0.75, fontWeight: 400, color: 'text.disabled' }}>|</Box> Internet Business Solutions
            </a>
          </Typography>

          <Typography
            variant="body1"
            sx={{ maxWidth: 620, mx: 'auto', mb: 2 }}
          >
            We build, deploy, and maintain the software systems your business runs on.
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 620, mx: 'auto', mb: 2 }}
          >
            From cloud architecture to custom applications to AI integration — enterprise-level
            expertise without enterprise-level bureaucracy. ShoutVector is one of those systems.
          </Typography>

          <Typography variant="body2">
            <a
              href="https://www.remagine.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit' }}
            >
              remagine.com
            </a>
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          {/*
            Points at "/" rather than "/login": signed out that redirects to the sign-in screen
            anyway, and signed in it lands on the Dashboard instead of a login form the visitor
            does not need. One label works for both.
          */}
          <Button component={RouterLink} to="/">
            Back to Home
          </Button>
        </Box>
      </Paper>
      <SiteFooter variant="standalone" />
    </Container>
  );
};
